import React, { useEffect, useRef, useState } from 'react';
import { Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { REBILL_PUBLIC_KEY, REBILL_PLAN_IDS } from '../config';
import { ensureRebillSDK, limpiarDescuentoRebill } from '../lib/rebill';

// Reemplaza a MercadoPagoCardPayment para clientes AR: renderiza el checkout
// embebido de Rebill (`<rebill-checkout>`). La pantalla de Checkout mantiene su
// propio detalle de precio, así que el resumen de Rebill va oculto por CSS —
// pero MONTADO, porque es lo único que auto-aplica el cupón (ver REBILL_STYLE_CSS).
// El backend procesa el webhook (activar plan, cancelar la sub vieja); el front
// solo dispara el checkout con la metadata (id_company, user_id).

// CSS inyectado al componente (prop `css`). Truco clave: usamos
// `checkoutSummary: true` para que el `rebill-summary` (y adentro el
// `discount-coupon`) se MONTE en el DOM — es lo único que auto-aplica el cupón
// (`coupon-code` → `initialCouponCode` → `componentDidLoad` → `setDiscount`).
// El auto-apply corre al montar, NO necesita estar visible; así que ocultamos el
// resumen por CSS (`display:none`, sigue montado) y el precio lo muestra el panel
// del Checkout. Además forzamos el form a ancho completo (una sola columna),
// porque en desktop el SDK arma 2 columnas con min-width 450+350 y se desborda en
// nuestro panel angosto. OJO: depende de clases internas del SDK (light DOM) —
// re-verificar si se actualiza `rebill`.
const REBILL_STYLE_CSS = `
  .rebill-checkout {
    flex-direction: column !important;
    background: transparent !important;
    padding: 0 !important;
    min-height: 0 !important;
  }
  .rebill-checkout .left-section { width: 100% !important; background: transparent !important; }
  .rebill-checkout .left-section-container {
    min-width: 0 !important;
    max-width: none !important;
    width: 100% !important;
    margin: 0.5rem 0 1rem 0 !important;
  }
  /* Resumen de Rebill: montado (para el cupón) pero oculto — el precio lo mostramos nosotros. */
  .rebill-checkout .right-section-checkout,
  .rebill-checkout .summary-mobile-tablet,
  .rebill-checkout .summary-price-details { display: none !important; }
`;

export interface RebillCheckoutResult {
  subscriptionId?: string;
  paymentId?: string;
}

export interface RebillPrefetchedCtx {
  /** Empresa/usuario para la metadata del webhook, o null si no se resolvió. */
  companyCtx: { idCompany: string; userId: string } | null;
  /** Prefill del form de Rebill (email/nombre), o null si no hay. */
  customerPrefill: { email?: string; fullName?: string } | null;
}

// Cuánto esperamos el `ready` del SDK antes de avisar la demora. Son ~3
// round-trips secuenciales a la API de Rebill; 15 s ya es una conexión muy mala.
const CHECKOUT_READY_TIMEOUT_MS = 15_000;

/**
 * ¿Ya se aplicó un cupón en ESTA carga de página? — ADR-2026-08-05-cupon-se-arrastra-entre-planes.
 *
 * Vive a nivel módulo, igual que el store del SDK de Rebill, y por la misma
 * razón: los dos sobreviven al desmontaje del `<rebill-checkout>` y sólo se
 * limpian con una carga real de página.
 *
 * El problema que resuelve: el `discount-coupon` del SDK, en su
 * `componentWillLoad`, RE-ADOPTA el descuento del store compartido…
 *
 *   if (state.data.pricing.discountCouponCode && state.data.pricing.discountAmount > 0) {
 *     this.actionStatus = ACTION_STATUS.APPLIED;
 *     this.couponCode = state.data.pricing.discountCouponCode;
 *   }
 *
 * …sin mirar el atributo `coupon-code` que le mandamos nosotros. O sea: nuestro
 * guard decide si MANDAMOS el cupón, pero no puede QUITAR uno ya aplicado. En
 * una SPA sin recarga, mirar Negocio (30% legítimo) y después elegir Básico se
 * lleva el descuento puesto. Pasó 2 veces en producción (21/7 y 5/8).
 *
 * El SDK no expone ningún reset PÚBLICO (`initializeRebillSDK` sólo configura
 * entorno y assets), pero su store interno sí es alcanzable:
 * `limpiarDescuentoRebill` lo limpia con el `setDiscount(null)` del propio SDK
 * y VERIFICA el resultado. La recarga de página quedó como red de seguridad
 * para cuando esa limpieza no se puede garantizar
 * (ADR-2026-08-24-limpiar-descuento-rebill-sin-recargar).
 */
let cuponAplicadoEnEstaPagina = false;

interface RebillCardPaymentProps {
  /** Plan de Atlas: 'emprendimiento' | 'negocio' | 'enterprise1' | ... */
  planId: string;
  /**
   * Código del cupón aplicado (ej. 'DESC30'), o null. Rebill lo auto-aplica.
   * `null` = este montaje exige PRECIO DE LISTA: si en esta página ya se aplicó
   * un cupón (otro plan), el descuento arrastrado en el store del SDK se limpia
   * antes de montar. El caller debe resolver el cupón ANTES de montar (no pasar
   * null "mientras carga"): un cambio posterior destruye y recrea el checkout.
   */
  couponCode?: string | null;
  /**
   * Contexto ya resuelto por la pantalla que nos monta. Si viene (aunque sus
   * campos sean null), acá NO se repiten las consultas de `getUser` + `profiles`
   * que esa pantalla ya hizo — menos latencia en el camino crítico del cobro.
   * `undefined` = consultarlo acá (EnterpriseCheckout, que no lo tiene a mano).
   */
  prefetchedCtx?: RebillPrefetchedCtx;
  onSuccess: (result: RebillCheckoutResult) => void;
  onError: (message: string) => void;
}

const RebillCardPayment: React.FC<RebillCardPaymentProps> = ({
  planId,
  couponCode,
  prefetchedCtx,
  onSuccess,
  onError,
}) => {
  const rebillPlanId = REBILL_PLAN_IDS[planId] || '';
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [companyCtx, setCompanyCtx] = useState<{
    idCompany: string;
    userId: string;
  } | null>(prefetchedCtx !== undefined ? prefetchedCtx.companyCtx : null);
  // Prefill del form de Rebill (`customer-information`): los campos visibles
  // arrancan cargados (editables) — el SDK los inyecta en su session store al
  // inicializar. Email = el de la cuenta (además es el que Rebill usa para
  // reconocer clientes existentes / mandar comprobantes).
  const [customerPrefill, setCustomerPrefill] = useState<{
    email?: string;
    fullName?: string;
  } | null>(prefetchedCtx !== undefined ? prefetchedCtx.customerPrefill : null);
  const [ctxResolved, setCtxResolved] = useState(prefetchedCtx !== undefined);
  // Estado de la inicialización del <rebill-checkout> — ADR-2026-08-24-checkout-
  // rebill-carga-silenciosa. El SDK no pinta NADA mientras su componentWillLoad
  // espera ~3 requests a la API de Rebill, y si alguno falla lo traga con un
  // console.error y el elemento queda vacío PARA SIEMPRE (su render() corta por
  // sessionState.isInitialized, y el evento `error` es solo de pagos). Estos
  // estados convierten eso en spinner → form, o en un error con "Reintentar".
  const [checkoutListo, setCheckoutListo] = useState(false); // `ready` + DOM con contenido
  const [checkoutRoto, setCheckoutRoto] = useState(false); // `ready` pero DOM vacío = init tragada
  const [checkoutLento, setCheckoutLento] = useState(false); // sin `ready` tras el timeout
  const hostRef = useRef<HTMLDivElement | null>(null);

  const configError = !REBILL_PUBLIC_KEY
    ? 'Falta configurar Rebill (VITE_REBILL_PUBLIC_KEY).'
    : !rebillPlanId
      ? 'Falta configurar el plan de Rebill para este plan.'
      : null;

  // Registro del SDK.
  useEffect(() => {
    if (configError) return;
    let mounted = true;
    ensureRebillSDK()
      .then(() => mounted && setSdkReady(true))
      .catch(
        (err) =>
          mounted && setSdkError((err as Error)?.message ?? 'Error cargando Rebill.'),
      );
    return () => {
      mounted = false;
    };
  }, [configError]);

  // Empresa/usuario logueado → metadata que viaja al webhook. Solo si la
  // pantalla que nos monta no lo resolvió ya (prefetchedCtx).
  useEffect(() => {
    if (prefetchedCtx !== undefined) return;
    if (configError) {
      setCtxResolved(true);
      return;
    }
    let mounted = true;
    (async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id_company, name')
            .eq('id', user.id)
            .single();
          const idCompany = (profile as any)?.id_company as string | null;
          if (mounted && idCompany) {
            setCompanyCtx({ idCompany, userId: user.id });
          }
          const fullName = ((profile as any)?.name as string | null)?.trim();
          if (mounted && (user.email || fullName)) {
            setCustomerPrefill({
              ...(user.email ? { email: user.email } : {}),
              ...(fullName ? { fullName } : {}),
            });
          }
        }
      } catch {
        /* sin empresa → seguimos sin metadata (el backend cae al email) */
      } finally {
        if (mounted) setCtxResolved(true);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [configError]);

  // Montaje imperativo del <rebill-checkout>: seteamos las props como ATRIBUTOS
  // ANTES de insertarlo (el SDK las lee en componentWillLoad al conectarse).
  useEffect(() => {
    if (configError || !sdkReady || !ctxResolved) return;
    const host = hostRef.current;
    if (!host) return;

    // Montaje nuevo = inicialización nueva: arrancamos esperando el `ready`.
    setCheckoutListo(false);
    setCheckoutRoto(false);
    setCheckoutLento(false);

    // Estado sucio: en esta página ya se aplicó un cupón y este montaje va SIN
    // cupón (plan que no admite descuento, o cupón que ya no está disponible).
    // El store del SDK le aplicaría el descuento arrastrado igual — se cobraría
    // de menos — así que primero se limpia por adentro y se VERIFICA
    // (`limpiarDescuentoRebill`). Solo si eso no se puede garantizar cae la
    // recarga de página original (ADR-2026-08-05-cupon-se-arrastra-entre-planes),
    // que era carísima: disparaba con el checkout ya cargado y el usuario pagaba
    // la carga entera dos veces con pantalla en blanco en el medio. No hace
    // loop: tras la recarga el flag vuelve a `false` (es estado de módulo).
    // Cuidado al tocar esto — sin limpieza VERIFICADA se cobra de menos.
    // Ver ADR-2026-08-24-limpiar-descuento-rebill-sin-recargar.
    if (!couponCode && cuponAplicadoEnEstaPagina) {
      if (limpiarDescuentoRebill()) {
        cuponAplicadoEnEstaPagina = false;
      } else {
        window.location.reload();
        return;
      }
    }
    if (couponCode) cuponAplicadoEnEstaPagina = true;

    const el = document.createElement('rebill-checkout') as any;
    el.setAttribute('public-key', REBILL_PUBLIC_KEY);
    el.setAttribute('plan-id', rebillPlanId);
    el.setAttribute('language', 'es');
    // checkoutSummary:true → el resumen se MONTA (necesario para que el cupón se
    // auto-aplique), pero lo ocultamos por CSS (ver REBILL_STYLE_CSS). El precio
    // lo muestra el panel del Checkout; el form queda a todo el ancho.
    // excludePaymentMethods: sin transferencia bancaria — una transferencia no
    // deja método guardado → sin débito automático de renovación (y el QR es
    // mala UX en mobile). Solo tarjeta.
    el.setAttribute(
      'display',
      JSON.stringify({
        logo: false,
        footer: false,
        checkoutSummary: true,
        excludePaymentMethods: ['bank_transfer'],
      }),
    );
    // Estilos para que combine con el panel del Checkout (fondo parejo, menos
    // espacio arriba). Ver REBILL_STYLE_CSS.
    el.setAttribute('css', REBILL_STYLE_CSS);
    // metadata para correlacionar el pago con la empresa. `customer: {}` es
    // obligatorio: sin él el SDK crashea en setPayment (accede customer.email).
    if (companyCtx) {
      el.setAttribute(
        'external-payment',
        JSON.stringify({
          customer: {},
          metadata: {
            id_company: companyCtx.idCompany,
            user_id: companyCtx.userId,
          },
        }),
      );
    }
    if (couponCode) el.setAttribute('coupon-code', couponCode);
    // Autocompletar nombre/email en el form (visibles y editables).
    if (customerPrefill) {
      el.setAttribute('customer-information', JSON.stringify(customerPrefill));
    }

    const onSuccessEv = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const r = detail?.data?.result ?? detail?.result ?? {};
      onSuccess({ subscriptionId: r.subscriptionId, paymentId: r.paymentId });
    };
    const onErrorEv = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      onError(
        (detail && (detail.message || detail.error)) ||
          'No pudimos procesar el pago. Probá con otra tarjeta.',
      );
    };
    // Salvaguarda: si esperábamos un cupón y Rebill lo rechazó (`discountApplied:
    // false` burbujea del discount-coupon interno), avisamos — si no, el panel
    // muestra precio con descuento pero el cobro saldría al precio pleno (el
    // código no existe/matchea en el panel de Rebill).
    const onDiscountEv = (e: Event) => {
      if (couponCode && (e as CustomEvent).detail === false) {
        onError(
          'No pudimos aplicar tu descuento. Escribinos antes de pagar para que lo revisemos.',
        );
      }
    };
    // ¿La inicialización terminó (y terminó BIEN)? El SDK emite `ready` en su
    // componentDidLoad, pero lo emite AUNQUE la init haya fallado (el catch de
    // componentWillLoad la traga). El éxito se distingue por el DOM: con la init
    // fallida su render() devuelve false y el elemento queda sin hijos. El
    // re-chequeo a los 500 ms da margen por si el primer render todavía no se
    // volcó al DOM cuando llega el evento.
    let cancelado = false;
    const timeoutLento = window.setTimeout(
      () => setCheckoutLento(true),
      CHECKOUT_READY_TIMEOUT_MS,
    );
    const onReadyEv = () => {
      const verificar = (intento: number) => {
        if (cancelado) return;
        if (el.childElementCount > 0) {
          window.clearTimeout(timeoutLento);
          setCheckoutListo(true);
          // Si el aviso de demora ya se mostró, se limpia solo: una conexión
          // lenta que al final llega es un éxito, no un error.
          setCheckoutLento(false);
          setCheckoutRoto(false);
        } else if (intento < 2) {
          window.setTimeout(() => verificar(intento + 1), 500);
        } else {
          window.clearTimeout(timeoutLento);
          setCheckoutRoto(true);
        }
      };
      verificar(0);
    };

    el.addEventListener('ready', onReadyEv);
    el.addEventListener('success', onSuccessEv);
    el.addEventListener('error', onErrorEv);
    el.addEventListener('discountApplied', onDiscountEv);
    host.appendChild(el);

    return () => {
      cancelado = true;
      window.clearTimeout(timeoutLento);
      el.removeEventListener('ready', onReadyEv);
      el.removeEventListener('success', onSuccessEv);
      el.removeEventListener('error', onErrorEv);
      el.removeEventListener('discountApplied', onDiscountEv);
      if (el.parentNode === host) host.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkReady, ctxResolved, rebillPlanId, couponCode, companyCtx, customerPrefill]);

  if (configError) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md flex items-center gap-2.5 text-[15px] text-red-700">
        <AlertCircle className="w-4 h-4 flex-shrink-0" />
        <span>{configError}</span>
      </div>
    );
  }
  if (sdkError) {
    // Recarga y no reintento in-app: además de reintentar el import, la recarga
    // limpia el store módulo del SDK (que sobrevive a la navegación SPA).
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-md text-[15px] text-red-700">
        <div className="flex items-center gap-2.5">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span>No pudimos cargar el formulario de pago. No se te cobró nada.</span>
        </div>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
        >
          Reintentar
        </button>
      </div>
    );
  }
  if (!sdkReady || !ctxResolved) {
    return (
      <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando checkout…
      </div>
    );
  }
  return (
    <div>
      {/* El SDK no pinta nada mientras inicializa (~3 requests a Rebill): el
          spinner queda hasta su `ready`, no hasta nuestro appendChild. */}
      {!checkoutListo && !checkoutRoto && !checkoutLento && (
        <div className="flex items-center justify-center gap-2 py-10 text-[15px] text-neutral-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando checkout…
        </div>
      )}
      {(checkoutRoto || (checkoutLento && !checkoutListo)) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-[15px] text-red-700">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>
              {checkoutRoto
                ? 'No pudimos cargar el formulario de pago. No se te cobró nada.'
                : 'El formulario de pago está tardando más de lo normal. Podés esperar unos segundos o reintentar.'}
            </span>
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="mt-3 inline-flex h-10 items-center justify-center rounded-md bg-brand-primary px-4 text-[15px] font-medium text-white shadow-sm transition-colors hover:bg-brand-primary-hover"
          >
            Reintentar
          </button>
        </div>
      )}
      <div ref={hostRef} />
    </div>
  );
};

export default RebillCardPayment;
